
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiClient from '@/lib/api/client';
import { VStack } from '@/components/core';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: 'admin' | 'root_admin' | 'partner_admin';
}

const EmailVerificationBlocker = ({ userEmail }: { userEmail: string }) => {
  const handleResendEmail = async () => {
    try {
      await apiClient.post('/auth/resend-verification');
      alert('Verifizierungsmail wurde erneut gesendet!');
    } catch {
      alert('Fehler beim Senden der Verifizierungsmail.');
    }
  };

  const handleRefreshStatus = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted to-black flex items-center justify-center p-token-md">
      <div className="bg-muted/50 border border-white/10 rounded-2xl p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-brand-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="w-8 h-8 text-brand-primary" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">E-Mail-Verifizierung erforderlich</h1>
        <p className="text-muted mb-6">
          Bitte bestätige deine E-Mail-Adresse, um fortzufahren. Wir haben eine Verifizierungsmail an <span className="text-white font-medium">{userEmail}</span> gesendet.
        </p>
        <VStack gap="sm">
          <Button
            onClick={handleRefreshStatus}
            className="w-full bg-brand-primary hover:bg-brand-primary/90"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Status aktualisieren
          </Button>
          <Button
            onClick={handleResendEmail}
            variant="outline"
            className="w-full border-white/20 text-white hover:bg-white/10"
          >
            Verifizierungsmail erneut senden
          </Button>
        </VStack>
        <p className="text-muted text-sm mt-6">
          Prüfe auch deinen Spam-Ordner, falls du keine E-Mail erhalten hast.
        </p>
      </div>
    </div>
  );
};

export const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();

  const { data: publicSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['public-settings'],
    queryFn: async () => {
      const res = await apiClient.get('/public/settings');
      return res.data as {
        default_credits: number;
        registrations_enabled: boolean;
        email_verification_required: boolean;
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  if (loading || settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check email verification if required
  const emailVerificationRequired = publicSettings?.email_verification_required !== false;
  if (emailVerificationRequired && !user.is_verified) {
    // Allow admins to bypass verification requirement
    if (user.role !== 'admin' && user.role !== 'root_admin' && user.role !== 'demo_admin') {
      return <EmailVerificationBlocker userEmail={user.email} />;
    }
  }

  if (requiredRole === 'admin' && user.role !== 'admin' && user.role !== 'root_admin' && user.role !== 'demo_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredRole === 'root_admin' && user.role !== 'root_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredRole === 'partner_admin' && user.role !== 'partner_admin' && user.role !== 'admin' && user.role !== 'root_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};
